import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useChat } from '../../hooks/useChat';

const SUGGESTED_PROMPTS = [
  "How do I write smart contracts with Daml?",
  "Explain the mint-burn equilibrium mechanism for Canton Coin",
  "How does Canton achieve atomic transactions across different applications?",
];

const SUGGESTED_PROMPTS_SHORT = [
  "How do I write smart contracts with Daml?",
  "Explain the mint-burn equilibrium",
  "How does Canton achieve atomic transactions?",
];

const WHITEPAPER_DOCS = [
  {
    title: "Canton Coin - MiCA Whitepaper",
    description: "The white paper for Canton Coin in accordance with Title II of Regulation (EU) 2023/1114 (MiCA).",
    url: "https://www.canton.network/hubfs/Canton%20Network%20Files/whitepapers/Canton%20Coin%20%20-%20MiCA%20Whitepaper.pdf",
  },
  {
    title: "Polyglot Canton Whitepaper",
    description: "Expanding to additional languages such as Solidity through Wasm, bridging DeFi with traditional finance.",
    url: "https://www.canton.network/hubfs/Canton%20Network%20Files/whitepapers/Polyglot_Canton_Whitepaper_11_02_25.pdf",
  },
  {
    title: "Canton Coin: A Responsible Approach",
    description: "Technology for a decentralized synchronizer with a native utility token to pay synchronizer traffic fees.",
    url: "https://www.canton.network/hubfs/Canton%20Coin%20A%20Responsible%20Approach%20to%20Digital%20Tokens.pdf",
  },
  {
    title: "Canton Coin: Payment Application",
    description: "Canton Coin incentivizes application builders, users, and infrastructure providers to use the Global Synchronizer.",
    url: "https://www.canton.network/hubfs/Canton%20Network%20Files/Documents%20(whitepapers,%20etc...)/Canton%20Coin_%20A%20Canton-Network-native%20payment%20application.pdf",
  },
  {
    title: "Canton Network Whitepaper",
    description: "The Canton Network resolves barriers to enterprise adoption by allowing each application to define its own privacy and governance.",
    url: "https://www.canton.network/hubfs/Canton/Canton%20Network%20-%20White%20Paper.pdf",
  },
  {
    title: "The Canton Protocol",
    description: "A blockchain protocol for linking Daml applications across participants and organizations.",
    url: "https://www.canton.network/hubfs/Canton/canton-whitepaper.pdf",
  },
];

export function ChatTab() {
  const {
    messages,
    isLoading,
    error,
    sendMessageStream,
    stopGeneration,
    regenerateLastResponse,
    conversations,
    currentConversationId,
    isLoadingConversations,
    loadConversation,
    newConversation,
    deleteConversation,
    renameConversation,
  } = useChat();

  const [input, setInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Start with sidebar closed on mobile
    return typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  });
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showWhitepapers, setShowWhitepapers] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const isAutoScrolling = useRef(true);

  // Scroll to bottom helper
  const scrollToBottom = (behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (behavior === 'instant') {
      container.scrollTop = container.scrollHeight;
    } else {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Check if user is near bottom of chat
  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  // Handle scroll events to show/hide scroll button
  const handleScroll = () => {
    const nearBottom = isNearBottom();
    setShowScrollButton(!nearBottom);
    isAutoScrolling.current = nearBottom;
  };

  // Auto-scroll when new messages arrive or during streaming
  useEffect(() => {
    if (isAutoScrolling.current) {
      scrollToBottom('smooth');
    }
  }, [messages]);

  // Reset auto-scroll when loading a new conversation
  useEffect(() => {
    isAutoScrolling.current = true;
    setShowScrollButton(false);
    // Scroll to bottom when conversation changes
    setTimeout(() => scrollToBottom('instant'), 50);
  }, [currentConversationId]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    sendMessageStream(input);
    setInput('');
    // Enable auto-scroll and scroll immediately when sending
    isAutoScrolling.current = true;
    setShowScrollButton(false);
    setTimeout(() => scrollToBottom('smooth'), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionClick = (prompt) => {
    if (isLoading) return;
    sendMessageStream(prompt);
    // Enable auto-scroll and scroll immediately when sending
    isAutoScrolling.current = true;
    setShowScrollButton(false);
    setTimeout(() => scrollToBottom('smooth'), 0);
  };

  return (
    <div
      className="chat-container"
      style={{
        display: 'flex',
        position: 'fixed',
        top: '60px',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50,
        background: '#0a0c10',
      }}
    >
      {/* Mobile overlay when sidebar is open */}
      <div
        className={`chat-sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <ConversationSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        conversations={conversations}
        currentConversationId={currentConversationId}
        isLoading={isLoadingConversations}
        onSelect={(id) => {
          loadConversation(id);
          // Close sidebar on mobile after selecting
          if (window.innerWidth <= 768) {
            setSidebarOpen(false);
          }
        }}
        onNew={() => {
          newConversation();
          // Close sidebar on mobile after creating new
          if (window.innerWidth <= 768) {
            setSidebarOpen(false);
          }
        }}
        onDelete={deleteConversation}
        onRename={renameConversation}
      />

      {/* Main Chat Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minWidth: 0,
      }}>
        {/* Toggle sidebar button (when closed) - matches position of button in sidebar header */}
        {!sidebarOpen && (
          <button
            className="chat-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            style={{
              position: 'fixed',
              top: '72px',
              left: '16px',
              zIndex: 10,
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: '#9ca3af',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.color = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
            title="Open sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="3" x2="9" y2="21"></line>
            </svg>
          </button>
        )}

        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            paddingBottom: '100px',
          }}
        >
          {messages.length === 0 ? (
            <EmptyState onSuggestionClick={handleSuggestionClick} showWhitepapers={showWhitepapers} setShowWhitepapers={setShowWhitepapers} />
          ) : (
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }}>
              {messages.map((msg, i) => (
                <MessageRow
                  key={i}
                  message={msg}
                  isLast={i === messages.length - 1}
                  onRegenerate={regenerateLastResponse}
                  isLoading={isLoading}
                />
              ))}

              {/* Show typing indicator only when loading and no streaming message yet */}
              {isLoading && !messages.some(m => m.isStreaming) && <TypingIndicator />}

              {error && (
                <div style={{
                  background: 'rgba(255, 71, 87, 0.1)',
                  border: '1px solid rgba(255, 71, 87, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  margin: '16px 0',
                  color: '#ff4757',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <span>Error: {error}</span>
                  <button
                    onClick={regenerateLastResponse}
                    style={{
                      background: 'rgba(255, 71, 87, 0.2)',
                      border: '1px solid rgba(255, 71, 87, 0.4)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#ff4757',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button
            onClick={() => {
              isAutoScrolling.current = true;
              scrollToBottom('smooth');
              // Delay hiding button until scroll animation starts
              setTimeout(() => setShowScrollButton(false), 100);
            }}
            style={{
              position: 'absolute',
              bottom: '140px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(15, 18, 25, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '20px',
              padding: '8px 16px',
              cursor: 'pointer',
              color: '#e5e7eb',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.15s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(30, 35, 45, 0.95)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(15, 18, 25, 0.95)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
            Scroll to bottom
          </button>
        )}

        {/* Input Area - Fixed at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(transparent, rgba(10, 12, 16, 0.95) 20%)',
          padding: '24px 16px 16px',
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <form
              onSubmit={handleSubmit}
              style={{
                background: 'rgba(15, 18, 25, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '8px 8px 8px 16px',
                display: 'flex',
                alignItems: 'flex-end',
                gap: '8px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
              }}
            >
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about Canton Network..."
                disabled={isLoading}
                rows={1}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 0',
                  fontSize: '15px',
                  color: '#e5e7eb',
                  outline: 'none',
                  resize: 'none',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  maxHeight: '200px',
                }}
              />
              {isLoading ? (
                <button
                  type="button"
                  onClick={stopGeneration}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#ff4757',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                  title="Stop generating"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim()}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    border: 'none',
                    background: !input.trim()
                      ? 'rgba(255, 255, 255, 0.05)'
                      : '#00ff88',
                    color: !input.trim() ? '#4b5563' : '#0a0c10',
                    cursor: !input.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              )}
            </form>

            <p style={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#4b5563',
              marginTop: '8px',
            }}>
              Your AI-powered guide to Canton Network intelligence
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConversationSidebar({
  isOpen,
  onToggle,
  conversations,
  currentConversationId,
  isLoading,
  onSelect,
  onNew,
  onDelete,
  onRename,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  if (!isOpen) return null;

  const handleRename = (id) => {
    if (editTitle.trim()) {
      onRename(id, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      className={`chat-sidebar ${isOpen ? 'open' : ''}`}
      style={{
        width: '280px',
        background: 'rgba(10, 12, 16, 0.98)',
        borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
      }}>
        <button
          onClick={onNew}
          style={{
            background: 'transparent',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            color: '#e5e7eb',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
            fontWeight: '400',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
          </svg>
          New chat
        </button>

        <button
          onClick={onToggle}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            color: '#9ca3af',
            flexShrink: 0,
            borderRadius: '8px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = '#e5e7eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#9ca3af';
          }}
          title="Close sidebar"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="9" y1="3" x2="9" y2="21"></line>
          </svg>
        </button>
      </div>

      {/* Conversations List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px',
      }}>
        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
            Loading...
          </div>
        ) : conversations.length === 0 ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '13px',
          }}>
            No conversations yet.<br />Start a new chat!
          </div>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv.id}
              style={{
                position: 'relative',
                marginBottom: '4px',
              }}
              onMouseEnter={() => setHoveredId(conv.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {editingId === conv.id ? (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '8px',
                }}>
                  <input
                    autoFocus
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename(conv.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onBlur={() => handleRename(conv.id)}
                    style={{
                      width: '100%',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(0, 255, 136, 0.3)',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      color: '#e5e7eb',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>
              ) : (
                <button
                  onClick={() => onSelect(conv.id)}
                  style={{
                    width: '100%',
                    background: conv.id === currentConversationId
                      ? 'rgba(0, 255, 136, 0.1)'
                      : 'transparent',
                    border: conv.id === currentConversationId
                      ? '1px solid rgba(0, 255, 136, 0.2)'
                      : '1px solid transparent',
                    borderRadius: '8px',
                    padding: '12px',
                    paddingRight: '50px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                  onMouseEnter={(e) => {
                    if (conv.id !== currentConversationId) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (conv.id !== currentConversationId) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{
                    fontSize: '13px',
                    color: '#e5e7eb',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {conv.title || 'New conversation'}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    display: 'flex',
                    gap: '8px',
                  }}>
                    <span>{formatDate(conv.updated_at)}</span>
                    {conv.message_count > 0 && (
                      <span>{conv.message_count} messages</span>
                    )}
                  </div>
                </button>
              )}

              {/* Actions (show on hover) */}
              {hoveredId === conv.id && editingId !== conv.id && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '8px',
                  display: 'flex',
                  gap: '2px',
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditTitle(conv.title || '');
                      setEditingId(conv.id);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#00ff88';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6b7280';
                    }}
                    title="Rename"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this conversation?')) {
                        onDelete(conv.id);
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#ff6b7a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6b7280';
                    }}
                    title="Delete"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function EmptyState({ onSuggestionClick, showWhitepapers, setShowWhitepapers }) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 480;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const prompts = isMobile ? SUGGESTED_PROMPTS_SHORT : SUGGESTED_PROMPTS;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: isMobile ? '20px 12px' : '40px 20px',
      textAlign: 'center',
    }}>
      {/* Whitepaper Hero Section - hidden on mobile */}
      {showWhitepapers && !isMobile && (
      <div style={{
        width: '100%',
        maxWidth: '900px',
        marginBottom: '32px',
        borderRadius: '16px',
        overflow: 'hidden',
      }}>
        {/* Hero Header with Background */}
        <div style={{
          backgroundImage: 'url(/hero-bg.png)',
          backgroundPosition: 'right center',
          backgroundSize: 'cover',
          padding: '20px 24px',
          textAlign: 'left',
          position: 'relative',
        }}>
          <button
            onClick={() => setShowWhitepapers(false)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: 'none',
              borderRadius: '6px',
              padding: '4px 10px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '11px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
            }}
          >
            Hide
          </button>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#ffffff',
            margin: '0 0 8px 0',
            lineHeight: '1.2',
          }}>
            Canton Network Whitepapers
          </h2>
          <p style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: 0,
          }}>
            Read these to understand the vision for Canton
          </p>
        </div>

        {/* Paper Cards Row */}
        <div style={{
          display: 'flex',
          background: '#f5f7fa',
        }}>
          {WHITEPAPER_DOCS.map((doc, i) => (
            <div
              key={i}
              onClick={(e) => {
                // Don't trigger if clicking the Read link
                if (e.target.tagName === 'A') return;
                onSuggestionClick(`Provide a concise summary of the paper "${doc.title}"`);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '14px 12px',
                flex: 1,
                minWidth: 0,
                borderRight: i < WHITEPAPER_DOCS.length - 1 ? '1px solid #e5e7eb' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.querySelector('.summarize-btn').style.color = '#5b21b6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.querySelector('.summarize-btn').style.color = '#7c3aed';
              }}
            >
              <h3 style={{
                fontSize: '12px',
                fontWeight: '700',
                color: '#1a1a1a',
                margin: '0 0 8px 0',
                lineHeight: '1.3',
                textAlign: 'left',
              }}>
                {doc.title}
              </h3>
              <p style={{
                fontSize: '10px',
                color: '#666666',
                lineHeight: '1.5',
                margin: '0 0 4px 0',
                flex: 1,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textAlign: 'left',
              }}>
                {doc.description}
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '10px',
              }}>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: '#7c3aed',
                    textDecoration: 'none',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  Read ›
                </a>
                <span
                  className="summarize-btn"
                  style={{
                    color: '#7c3aed',
                    fontSize: '10px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    transition: 'color 0.15s ease',
                  }}
                >
                  Summarize ↓
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Logo/Icon */}
      <img
        onClick={() => onSuggestionClick('WAGMI!')}
        src="/logo_hq_v2.png"
        alt="Cantonstats AI"
        style={{
          width: isMobile ? '60px' : '80px',
          height: isMobile ? '60px' : '80px',
          marginBottom: isMobile ? '16px' : '24px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          filter: 'grayscale(100%) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))',
          opacity: 0.8,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      />

      <h2 style={{
        fontSize: isMobile ? '20px' : '24px',
        fontWeight: '600',
        color: '#e5e7eb',
        marginBottom: '8px',
        textShadow: '0 0 12px rgba(255, 255, 255, 0.4)',
      }}>Cantonstats AI</h2>

      <p style={{
        fontSize: isMobile ? '13px' : '15px',
        color: '#6b7280',
        marginBottom: isMobile ? '20px' : '32px',
        maxWidth: '400px',
        padding: isMobile ? '0 8px' : 0,
      }}>
        Your AI-powered guide to Canton Network. Ask about validators, transactions, CC rewards, and protocol documentation.
      </p>

      {/* Suggested prompts */}
      <div style={{
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : undefined,
        gridTemplateColumns: isMobile ? undefined : 'repeat(3, 1fr)',
        gap: isMobile ? '8px' : '12px',
        maxWidth: isMobile ? '100%' : '700px',
        width: '100%',
      }}>
        {prompts.map((prompt, i) => (
          <button
            key={i}
            onClick={() => onSuggestionClick(SUGGESTED_PROMPTS[i])}
            style={{
              background: 'rgba(15, 18, 25, 0.8)',
              border: 'none',
              borderRadius: '12px',
              padding: isMobile ? '12px 14px' : '16px',
              textAlign: 'left',
              color: '#9ca3af',
              fontSize: isMobile ? '13px' : '14px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              lineHeight: '1.4',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(0, 255, 136, 0.08)';
              e.target.style.color = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(15, 18, 25, 0.8)';
              e.target.style.color = '#9ca3af';
            }}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageRow({ message, isLast, onRegenerate, isLoading }) {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showActions = !isUser && !isStreaming && message.content;

  return (
    <div style={{
      display: 'flex',
      gap: '16px',
      padding: '24px 0',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    }}>
      {/* Avatar */}
      {isUser ? (
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '6px',
          background: '#1a1d24',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: '600',
          color: '#e5e7eb',
        }}>
          Y
        </div>
      ) : (
        <img
          src="/logo_hq_v2.png"
          alt="AI"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            flexShrink: 0,
            filter: 'grayscale(100%)',
            opacity: 0.8,
          }}
        />
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#9ca3af',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>{isUser ? 'You' : 'Cantonstats AI'}</span>

          {/* Action buttons for assistant messages */}
          {showActions && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleCopy}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: copied ? '#00ff88' : '#6b7280',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  borderRadius: '4px',
                }}
                title="Copy response"
              >
                {copied ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy
                  </>
                )}
              </button>

              {isLast && !isLoading && (
                <button
                  onClick={onRegenerate}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: '4px',
                  }}
                  title="Regenerate response"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  Regenerate
                </button>
              )}
            </div>
          )}
        </div>

        {isUser ? (
          <div style={{
            color: '#e5e7eb',
            fontSize: '15px',
            lineHeight: '1.7',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {message.content}
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {message.content ? (
              <MarkdownContent content={message.content} />
            ) : isStreaming ? (
              <StreamingPlaceholder />
            ) : null}
            {isStreaming && message.content && <StreamingCursor />}
            {message.wasStopped && (
              <div style={{
                marginTop: '12px',
                fontSize: '12px',
                color: '#9ca3af',
                fontStyle: 'italic',
              }}>
                (Generation stopped)
              </div>
            )}
          </div>
        )}

        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <div style={{
            marginTop: '16px',
            paddingTop: '12px',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          }}>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              Sources
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {message.sources.map((source, i) => (
                <span
                  key={i}
                  style={{
                    background: 'rgba(0, 255, 136, 0.1)',
                    border: '1px solid rgba(0, 255, 136, 0.2)',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#00ff88',
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  {source}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MarkdownContent({ content }) {
  return (
    <div className="markdown-content" style={{
      color: '#e5e7eb',
      fontSize: '15px',
      lineHeight: '1.7',
    }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code blocks with syntax highlighting
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const codeString = String(children).replace(/\n$/, '');

            if (!inline && (match || codeString.includes('\n'))) {
              return (
                <div style={{ position: 'relative', margin: '16px 0' }}>
                  {match && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '12px',
                      fontSize: '11px',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                    }}>
                      {match[1]}
                    </div>
                  )}
                  <SyntaxHighlighter
                    style={oneDark}
                    language={match ? match[1] : 'text'}
                    PreTag="div"
                    customStyle={{
                      margin: 0,
                      borderRadius: '8px',
                      padding: '16px',
                      fontSize: '13px',
                      background: 'rgba(0, 0, 0, 0.4)',
                    }}
                    {...props}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                  <button
                    onClick={() => navigator.clipboard.writeText(codeString)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: match ? '60px' : '12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      color: '#9ca3af',
                      cursor: 'pointer',
                    }}
                  >
                    Copy
                  </button>
                </div>
              );
            }

            return (
              <code
                style={{
                  background: 'rgba(0, 255, 136, 0.1)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontFamily: '"JetBrains Mono", monospace',
                  color: '#00ff88',
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
          // Paragraphs
          p({ children }) {
            return <p style={{ margin: '0 0 16px 0' }}>{children}</p>;
          },
          // Headers
          h1({ children }) {
            return <h1 style={{ fontSize: '24px', fontWeight: '600', margin: '24px 0 16px 0', color: '#fff' }}>{children}</h1>;
          },
          h2({ children }) {
            return <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '20px 0 12px 0', color: '#fff' }}>{children}</h2>;
          },
          h3({ children }) {
            return <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '16px 0 8px 0', color: '#fff' }}>{children}</h3>;
          },
          // Lists
          ul({ children }) {
            return <ul style={{ margin: '0 0 16px 0', paddingLeft: '24px' }}>{children}</ul>;
          },
          ol({ children }) {
            return <ol style={{ margin: '0 0 16px 0', paddingLeft: '24px' }}>{children}</ol>;
          },
          li({ children }) {
            return <li style={{ marginBottom: '4px' }}>{children}</li>;
          },
          // Blockquotes
          blockquote({ children }) {
            return (
              <blockquote style={{
                borderLeft: '3px solid #00ff88',
                paddingLeft: '16px',
                margin: '16px 0',
                color: '#9ca3af',
                fontStyle: 'italic',
              }}>
                {children}
              </blockquote>
            );
          },
          // Tables
          table({ children }) {
            return (
              <div style={{ overflowX: 'auto', margin: '16px 0' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                }}>
                  {children}
                </table>
              </div>
            );
          },
          th({ children }) {
            return (
              <th style={{
                background: 'rgba(0, 255, 136, 0.1)',
                padding: '10px 12px',
                textAlign: 'left',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                fontWeight: '600',
                color: '#00ff88',
              }}>
                {children}
              </th>
            );
          },
          td({ children }) {
            return (
              <td style={{
                padding: '10px 12px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              }}>
                {children}
              </td>
            );
          },
          // Links
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#00ff88',
                  textDecoration: 'none',
                }}
              >
                {children}
              </a>
            );
          },
          // Horizontal rule
          hr() {
            return <hr style={{ border: 'none', borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '24px 0' }} />;
          },
          // Strong/bold
          strong({ children }) {
            return <strong style={{ color: '#fff', fontWeight: '600' }}>{children}</strong>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function StreamingPlaceholder() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280' }}>
      <div className="streaming-dot" style={{ animationDelay: '0s' }} />
      <div className="streaming-dot" style={{ animationDelay: '0.15s' }} />
      <div className="streaming-dot" style={{ animationDelay: '0.3s' }} />
      <style>{`
        .streaming-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #00ff88;
          animation: streaming-pulse 1.4s ease-in-out infinite;
        }
        @keyframes streaming-pulse {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function StreamingCursor() {
  return (
    <span
      className="streaming-cursor"
      style={{
        display: 'inline-block',
        width: '2px',
        height: '1em',
        background: '#00ff88',
        marginLeft: '2px',
        verticalAlign: 'text-bottom',
      }}
    >
      <style>{`
        .streaming-cursor {
          animation: cursor-blink 0.8s ease-in-out infinite;
        }
        @keyframes cursor-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </span>
  );
}

function TypingIndicator() {
  return (
    <div style={{
      display: 'flex',
      gap: '16px',
      padding: '24px 0',
    }}>
      {/* Avatar */}
      <img
        src="/logo_hq_v2.png"
        alt="AI"
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          flexShrink: 0,
          filter: 'grayscale(100%)',
          opacity: 0.8,
        }}
      />

      {/* Typing dots */}
      <div style={{ paddingTop: '12px' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div className="typing-dot" style={{ animationDelay: '0s' }} />
          <div className="typing-dot" style={{ animationDelay: '0.15s' }} />
          <div className="typing-dot" style={{ animationDelay: '0.3s' }} />
        </div>
        <style>{`
          .typing-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #00ff88;
            animation: typing-bounce 1.4s ease-in-out infinite;
          }
          @keyframes typing-bounce {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.4;
            }
            30% {
              transform: translateY(-8px);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

